import { Controller } from "@hotwired/stimulus"

// Global full-page loader. Shown when:
//   * the user clicks a same-origin link (non-download, non-new-tab, non-modifier)
//   * a form is submitted (capture phase — runs before the browser leaves the page)
//   * Turbo issues a visit / form submission (if Turbo is enabled for that link)
//
// The preloader partial separately hides the initial-load spinner via Alpine,
// so this controller only handles subsequent navigations.
export default class extends Controller {
  static targets = ["overlay", "label"]

  connect() {
    this.onClick = this.handleClick.bind(this)
    this.onSubmit = this.handleSubmit.bind(this)
    // Wrap in arrows so the Turbo event object is NOT forwarded as show()'s
    // customLabel arg (which would render the overlay text as "[object Object]").
    this.onTurboVisit = () => this.show()
    this.onTurboSubmitStart = () => this.show()
    // Lazy/eager <turbo-frame> loads aren't visits or submits, so opt a frame in
    // with [data-loader-frame] to reuse this overlay instead of its own placeholder.
    this.onFrameFetch = (event) => {
      const frame = event.target
      if (frame instanceof Element && frame.matches("[data-loader-frame]")) this.showWithSafetyTimer()
    }
    this.onTurboComplete = () => this.hide()
    this.onPageShow = this.handlePageShow.bind(this)
    this.safetyTimer = null

    document.addEventListener("click", this.onClick, true)
    document.addEventListener("submit", this.onSubmit, true)
    document.addEventListener("turbo:visit", this.onTurboVisit)
    document.addEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.addEventListener("turbo:before-fetch-request", this.onFrameFetch)

    // Completion events — any one of these means a navigation finished.
    document.addEventListener("turbo:submit-end", this.onTurboComplete)
    document.addEventListener("turbo:load", this.onTurboComplete)
    document.addEventListener("turbo:render", this.onTurboComplete)
    document.addEventListener("turbo:frame-load", this.onTurboComplete)
    document.addEventListener("turbo:frame-missing", this.onTurboComplete)
    document.addEventListener("turbo:fetch-request-error", this.onTurboComplete)
    document.addEventListener("turbo:before-cache", this.onTurboComplete)
    // A page that keeps the user on it after a link click (e.g. "unsaved changes? Stay") says so.
    document.addEventListener("navigation-loader:cancel", this.onTurboComplete)

    // bfcache restore: hide the overlay if the user came back via back/forward.
    window.addEventListener("pageshow", this.onPageShow)
  }

  disconnect() {
    document.removeEventListener("click", this.onClick, true)
    document.removeEventListener("submit", this.onSubmit, true)
    document.removeEventListener("turbo:visit", this.onTurboVisit)
    document.removeEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.removeEventListener("turbo:before-fetch-request", this.onFrameFetch)
    document.removeEventListener("turbo:submit-end", this.onTurboComplete)
    document.removeEventListener("turbo:load", this.onTurboComplete)
    document.removeEventListener("turbo:render", this.onTurboComplete)
    document.removeEventListener("turbo:frame-load", this.onTurboComplete)
    document.removeEventListener("turbo:frame-missing", this.onTurboComplete)
    document.removeEventListener("turbo:fetch-request-error", this.onTurboComplete)
    document.removeEventListener("turbo:before-cache", this.onTurboComplete)
    document.removeEventListener("navigation-loader:cancel", this.onTurboComplete)
    window.removeEventListener("pageshow", this.onPageShow)
    this.clearSafetyTimer()
  }

  handleClick(event) {
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button && event.button !== 0) return

    const anchor = event.target.closest("a[href]")
    if (!anchor) return
    if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return
    if (anchor.hasAttribute("download")) return
    if (anchor.dataset.noLoader === "true") return
    // A confirm dialog may cancel this navigation, and Turbo fires no event
    // when it does. Wait for turbo:visit / turbo:submit-start (dispatched only
    // after the user confirms) rather than showing the loader optimistically.
    if (anchor.dataset.turboConfirm != null) return

    const href = anchor.getAttribute("href")
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return
    if (href.startsWith("mailto:") || href.startsWith("tel:")) return

    try {
      const url = new URL(href, window.location.href)
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return
    } catch (_) {
      return
    }

    // Defer: other capture-phase handlers (e.g. Stimulus actions calling
    // event.preventDefault()) run before the microtask drains. If they cancel
    // the click, we must not show the loader.
    queueMicrotask(() => {
      if (event.defaultPrevented) return
      this.showWithSafetyTimer()
    })
  }

  handleSubmit(event) {
    const form = event.target
    if (!form || form.dataset.noLoader === "true") return
    if (form.dataset.remote === "true") return
    // Skip while a confirm dialog is pending (see handleClick) — the loader is
    // shown later by turbo:submit-start if the user confirms.
    const submitter = event.submitter
    if (form.dataset.turboConfirm != null || submitter?.dataset.turboConfirm != null) return

    queueMicrotask(() => {
      if (event.defaultPrevented) return
      this.showWithSafetyTimer()
    })
  }

  showWithSafetyTimer() {
    this.show()
    this.clearSafetyTimer()
    // Last-resort: never leave the overlay up for more than 15s even if a
    // completion event somehow never fires.
    this.safetyTimer = window.setTimeout(() => this.hide(), 15000)
  }

  clearSafetyTimer() {
    if (this.safetyTimer) {
      window.clearTimeout(this.safetyTimer)
      this.safetyTimer = null
    }
  }

  handlePageShow(event) {
    if (event.persisted) this.hide()
  }

  show(customLabel) {
    if (!this.hasOverlayTarget) return
    if (typeof customLabel === "string" && customLabel && this.hasLabelTarget) this.labelTarget.textContent = customLabel
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")
  }

  hide() {
    this.clearSafetyTimer()
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
  }
}
